import { useFormContext } from '@sravni/cosago-react-library/lib/hooks';
import type { IFieldFactoryProps } from '@sravni/cosago-react-library/lib/types';
import { Dropdown, TextInput, Button, Icon, Typography } from '@sravni/react-design-system';
import type { SelectOption, SelectValue } from '@sravni/react-design-system/lib/Select';
import { Cross } from '@sravni/react-icons';
import type { FormEvent } from 'react';
import { useRef, useState, useEffect, useCallback, useMemo } from 'react';

import { useGetOrganizationSuggestions } from 'shared/api/daData/dadata.query';
import { sendCustomAnalyticsEvent } from 'shared/lib/sendCustomAnalyticsEvent';
import { useFetchSuggestions } from 'shared/lib/useFetchSuggestions';
import { useValidateOnBlur } from 'shared/lib/useValidateOnBlur';
import type { TOrganizationData } from 'shared/types/TOrganizationFields';

import type { TWorkplaceFormFields } from 'entities/creditForm';
import { useAllFormFields, useDictionaries } from 'entities/creditForm';
import { convertDadataOrganizationToFormFields } from 'entities/creditForm/lib/organizationData';

import { useUpdateWorkFields } from 'features/UpdateWorkplaceInfo/lib/useUpdateWorkFields';

import { FillManuallyOption, FillManuallyOptionConstants } from '../../FillManuallyOption';

import { WorkOrganizationNameConstants } from './WorkOrganizationName.constants';
import styles from './WorkOrganizationName.module.scss';

export const WorkOrganizationName: FC<IFieldFactoryProps> = ({ type, label }) => {
  const { updateWorkFields, resetFields } = useUpdateWorkFields();
  const { watch, formState } = useFormContext<TWorkplaceFormFields>();
  const { errors } = formState;
  const workOrganizationName = watch('workOrganizationName');
  const initialValue = useRef(workOrganizationName);
  const dictionaries = useDictionaries(['areasOfEmployment', 'employerTypes']);
  const fields = useAllFormFields();
  const fieldRef = useRef<HTMLDivElement | null>(null);

  const [getOrganizationSuggestions] = useGetOrganizationSuggestions();
  const [isInitialSuggestsLoaded, setIsInitialSuggestsLoaded] = useState(false);

  const getOrganizationSuggests = useCallback(
    async (value: string) => {
      const response = await getOrganizationSuggestions(value, true);
      const suggestions = response.data || [];

      if (!isInitialSuggestsLoaded) {
        setIsInitialSuggestsLoaded(true);
        const initialSuggest = initialValue.current
          ? suggestions.find((suggest) => suggest.value === initialValue.current)
          : undefined;

        if (initialValue && suggestions.length && initialSuggest && initialSuggest.data) {
          const updateData = convertDadataOrganizationToFormFields(
            { data: initialSuggest.data, label: initialSuggest.label, value: initialSuggest.label },
            fields,
            dictionaries,
          );

          updateWorkFields(updateData, true);
        }
      }

      return suggestions;
    },
    [initialValue, isInitialSuggestsLoaded, fields, dictionaries, updateWorkFields, getOrganizationSuggestions],
  );

  const { setSearchValue, isLoading, currentSuggestions, searchValue } = useFetchSuggestions<TOrganizationData['data']>(
    getOrganizationSuggests,
    workOrganizationName,
    true,
  );

  useEffect(() => {
    if (workOrganizationName && searchValue !== workOrganizationName) {
      setSearchValue(workOrganizationName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrganizationName]);

  const isManuallyFilled = watch('isWorkOrganizationManuallyFilled');
  const validate = useValidateOnBlur(type, true);

  const handleOnChangeInput = useCallback(
    (event: FormEvent<HTMLInputElement>) => {
      setSearchValue(event.currentTarget.value);
      if (isManuallyFilled) {
        updateWorkFields({
          workOrganizationName: event.currentTarget.value,
          workAddress: null,
          workPhone: '',
          employerInn: '',
          areaOfEmployment: null,
          employerType: null,
        });
      }
    },
    [setSearchValue, isManuallyFilled, updateWorkFields],
  );

  const options: Array<SelectOption & { data?: TOrganizationData['data'] }> = useMemo(
    () => [
      ...(searchValue.length >= WorkOrganizationNameConstants.MinimalLengthForRequest && !isLoading
        ? [FillManuallyOption]
        : []),
      ...currentSuggestions.map((item, index) => {
        const description = `ИНН ${item.data?.inn || ''}, ${item.data?.address?.value || ''}`;
        return {
          label: item.value?.toString(),
          value: `${index}${item.value}`,
          description,
          data: item.data,
        };
      }),
    ],
    [currentSuggestions, searchValue, isLoading],
  );

  const handleOnSelect = useCallback(
    (value: SelectValue) => {
      if (value === FillManuallyOptionConstants.ManualOptionValue) {
        updateWorkFields({
          workOrganizationName: searchValue,
          isWorkOrganizationManuallyFilled: true,
        });
        return;
      }
      const option = options.find((item) => item.value === value);
      if (!option || !option.data) {
        return;
      }

      const updateData = convertDadataOrganizationToFormFields(
        { data: option.data, label: option.label, value: option.label },
        fields,
        dictionaries,
      );

      updateWorkFields({ ...updateData, isWorkOrganizationManuallyFilled: false }, true);
      setSearchValue(option.label);
      sendCustomAnalyticsEvent(fieldRef.current, 'onChange', {
        fieldName: 'workOrganizationName',
        newValue: option.label,
        previousValue: undefined,
      });
    },
    [options, searchValue, setSearchValue, dictionaries, fields, updateWorkFields],
  );

  const handleOnClear = useCallback(() => {
    setSearchValue('');
    resetFields();
  }, [resetFields, setSearchValue]);

  const isShowClearButton = Boolean(searchValue);
  const isInvalidField = Boolean(errors.workOrganizationName);
  const errorText = errors.workOrganizationName?.message;

  return (
    <div
      onBlur={validate}
      ref={fieldRef}
    >
      <Dropdown
        options={options}
        placement="bottom-start"
        matchWidth
        onChange={handleOnSelect}
        emptyContent={null}
        value={searchValue}
      >
        <div className={styles.inputWrapper}>
          <TextInput
            loading={isLoading}
            label={label}
            name={type}
            value={searchValue}
            onChange={handleOnChangeInput}
            invalid={isInvalidField}
            icon={
              isShowClearButton && (
                <Button
                  type="button"
                  variant="text"
                  onClick={handleOnClear}
                  className={styles.clearButton}
                >
                  <Icon
                    icon={<Cross />}
                    size={24}
                  />
                </Button>
              )
            }
          />
          {errorText && (
            <Typography.Text
              size={12}
              className={styles.errorText}
            >
              {errorText}
            </Typography.Text>
          )}
        </div>
      </Dropdown>
    </div>
  );
};
